package com.schoolconnect.app.repository.academicyear

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.AcademicYearCreateRequest
import com.schoolconnect.app.datasource.network.request.AcademicYearUpdateRequest
import com.schoolconnect.app.datasource.network.response.AcademicYearAnalyticsResponse
import com.schoolconnect.app.datasource.network.response.AcademicYearResponse
import com.schoolconnect.app.datasource.network.response.AcademicYearWithSchoolResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.util.scLog
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object AcademicYearRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] AcademicYearRepository"

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class AcademicYearsPageCacheKey(
        val schoolId: String?,
        val schoolName: String?,
        val lifecycle: String?,
        val query: String,
        val startYear: Int?,
        val endYear: Int?,
        val createdFrom: String,
        val createdTo: String,
        val isActive: Boolean?,
        val sort: String
    )

    private data class CachedAcademicYearsPage(
        val key: AcademicYearsPageCacheKey,
        val years: List<AcademicYearWithSchoolResponse>
    )

    private data class AcademicYearsAnalyticsCacheKey(
        val schoolId: String?
    )

    private data class CachedAcademicYearsAnalytics(
        val key: AcademicYearsAnalyticsCacheKey,
        val analytics: CachedValue<AcademicYearAnalyticsResponse>
    )

    private var cachedAcademicYearsPage: CachedAcademicYearsPage? = null
    private var cachedAcademicYearsAnalytics: CachedAcademicYearsAnalytics? = null

    suspend fun loadAcademicYearPage(
        schoolId: String? = null,
        schoolName: String? = null,
        lifecycle: String? = null,
        query: String = "",
        startYear: Int? = null,
        endYear: Int? = null,
        createdFrom: String = "",
        createdTo: String = "",
        isActive: Boolean? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("start_date", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<AcademicYearWithSchoolResponse>> {
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val normalizedQuery = query.trim()
        val normalizedSchoolName = schoolName?.trim()?.takeIf { it.isNotBlank() }
        val isYearSearch = normalizedQuery.isNotBlank() && normalizedQuery.all {
            it.isDigit() || it == '-' || it.isWhitespace()
        }
        val normalizedCreatedFrom = createdFrom.trim()
        val normalizedCreatedTo = createdTo.trim()
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = AcademicYearsPageCacheKey(
            schoolId = normalizedSchoolId,
            schoolName = normalizedSchoolName?.lowercase(),
            lifecycle = lifecycle?.trim()?.takeIf { it.isNotBlank() },
            query = normalizedQuery,
            startYear = startYear,
            endYear = endYear,
            createdFrom = normalizedCreatedFrom,
            createdTo = normalizedCreatedTo,
            isActive = isActive,
            sort = sort.cacheKey
        )

        cachedAcademicYearsPage?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadAcademicYearPage cacheHit school=${normalizedSchoolId ?: "<any>"} schoolName=${normalizedSchoolName ?: "<any>"} lifecycle=${lifecycle ?: "<any>"} query=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.years.size} total=${cache.years.serverTotalCountOrNull() ?: "<unknown>"}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadAcademicYearPage", "pageIndex=$safePageIndex pageSize=$safePageSize", cache.years.page(safePageIndex, safePageSize)) {
                loadAcademicYearPage(
                    schoolId = normalizedSchoolId,
                    schoolName = normalizedSchoolName,
                    lifecycle = lifecycle,
                    query = query,
                    startYear = startYear,
                    endYear = endYear,
                    createdFrom = normalizedCreatedFrom,
                    createdTo = normalizedCreatedTo,
                    isActive = isActive,
                    pageIndex = safePageIndex,
                    pageSize = safePageSize,
                    sort = sort,
                    forceRemote = true
                )
            }
        }

        scLog(
            "$TAG loadAcademicYearPage start school=${normalizedSchoolId ?: "<any>"} schoolName=${normalizedSchoolName ?: "<any>"} lifecycle=${lifecycle ?: "<any>"} query=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize forceRemote=$forceRemote"
        )
        return runCatching {
            val response = KtorApiClient.getAcademicYearsWithSchoolName(
                schoolId = normalizedSchoolId,
                schoolName = normalizedSchoolName
                    ?: normalizedQuery.takeIf { it.isNotBlank() && !isYearSearch },
                lifecycle = lifecycle,
                query = normalizedQuery.takeIf { it.isNotBlank() && isYearSearch },
                startYear = startYear,
                endYear = endYear,
                createdFrom = normalizedCreatedFrom.ifBlank { null },
                createdTo = normalizedCreatedTo.ifBlank { null },
                isActive = isActive,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).getOrElse { throw it }
            val allYears = response.distinctBy { it.id }
                .withServerTotalCount(response.serverTotalCountOrNull())
            cachedAcademicYearsPage = CachedAcademicYearsPage(cacheKey, allYears)
            val page = allYears.page(safePageIndex, safePageSize)
            scLog("$TAG loadAcademicYearPage complete count=${page.size} total=${allYears.serverTotalCountOrNull() ?: "<unknown>"} pageIndex=$safePageIndex pageSize=$safePageSize")
            page
        }.onFailure { throwable ->
            scLog("$TAG loadAcademicYearPage failure school=${normalizedSchoolId ?: "<any>"} query=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize", throwable)
        }
    }

    private fun List<AcademicYearWithSchoolResponse>.page(
        pageIndex: Int,
        pageSize: Int
    ): List<AcademicYearWithSchoolResponse> {
        val start = (pageIndex * pageSize).coerceAtMost(size)
        val end = (start + pageSize).coerceAtMost(size)
        return subList(start, end).withServerTotalCount(serverTotalCountOrNull())
    }

    suspend fun loadAcademicYears(
        schoolId: String? = null,
        lifecycle: String? = null,
        query: String = "",
        startYear: Int? = null,
        endYear: Int? = null,
        createdFrom: String = "",
        createdTo: String = "",
        forceRemote: Boolean = false
    ): Result<List<AcademicYearWithSchoolResponse>> {
        return loadAcademicYearPage(
            schoolId = schoolId,
            lifecycle = lifecycle,
            query = query,
            startYear = startYear,
            endYear = endYear,
            createdFrom = createdFrom,
            createdTo = createdTo,
            pageIndex = 0,
            pageSize = 20,
            forceRemote = forceRemote
        )
    }

    suspend fun createAcademicYear(request: AcademicYearCreateRequest): Result<AcademicYearResponse> {
        scLog(
            "$TAG createAcademicYear start schoolId=${request.schoolId.ifBlank { "<blank>" }} label=${request.label.ifBlank { "<blank>" }}"
        )
        return runCatching {
            val createdYear = KtorApiClient.createAcademicYear(request).getOrElse { throw it }
            invalidateCaches()
            scLog(
                "$TAG createAcademicYear complete id=${createdYear.id.orEmpty()} schoolId=${createdYear.schoolId.orEmpty().ifBlank { request.schoolId }}"
            )
            createdYear
        }
    }

    suspend fun updateAcademicYear(
        academicYearId: String,
        request: AcademicYearUpdateRequest
    ): Result<AcademicYearResponse> {
        val normalizedId = academicYearId.trim()
        scLog("$TAG updateAcademicYear start academicYearId=$normalizedId")
        return runCatching {
            val updatedYear = KtorApiClient.updateAcademicYear(normalizedId, request).getOrElse { throw it }
            invalidateCaches()
            scLog("$TAG updateAcademicYear complete academicYearId=$normalizedId")
            updatedYear
        }
    }

    suspend fun updateAcademicYearStatus(
        academicYearId: String,
        isActive: Boolean
    ): Result<Unit> {
        val normalizedId = academicYearId.trim()
        scLog(
            "$TAG updateAcademicYearStatus start academicYearId=$normalizedId isActive=$isActive"
        )
        return runCatching {
            KtorApiClient.toggleAcademicYearActive(normalizedId, isActive).getOrElse { throw it }
            invalidateCaches()
            scLog(
                "$TAG updateAcademicYearStatus complete academicYearId=$normalizedId isActive=$isActive"
            )
        }
    }

    suspend fun loadCurrentAcademicYear(schoolId: String): Result<AcademicYearResponse?> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) return Result.failure(IllegalArgumentException("schoolId is required"))
        scLog("$TAG loadCurrentAcademicYear start school=$normalizedSchoolId")
        return KtorApiClient.getCurrentAcademicYear(normalizedSchoolId)
            .onSuccess { year ->
                scLog("$TAG loadCurrentAcademicYear complete school=$normalizedSchoolId found=${year != null}")
            }
            .onFailure { throwable ->
                scLog("$TAG loadCurrentAcademicYear failure school=$normalizedSchoolId", throwable)
            }
    }

    suspend fun deleteAcademicYear(academicYearId: String): Result<Unit> {
        val normalizedId = academicYearId.trim()
        scLog("$TAG deleteAcademicYear start academicYearId=$normalizedId")
        return runCatching {
            KtorApiClient.deleteAcademicYear(normalizedId).getOrElse { throw it }
            invalidateCaches()
            scLog("$TAG deleteAcademicYear complete academicYearId=$normalizedId")
        }
    }

    fun invalidateCaches() {
        cachedAcademicYearsPage = null
        cachedAcademicYearsAnalytics = null
    }

    suspend fun loadAcademicYearAnalytics(
        schoolId: String? = null,
        forceRemote: Boolean = false
    ): Result<AcademicYearAnalyticsResponse> {
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        val cacheKey = AcademicYearsAnalyticsCacheKey(schoolId = normalizedSchoolId)

        cachedAcademicYearsAnalytics?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadAcademicYearAnalytics cacheHit school=${normalizedSchoolId ?: "<any>"} total=${cache.analytics.value.metrics.totalAcademicYears}"
            )
            return Result.success(cache.analytics.value)
        }

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadAcademicYearAnalytics",
            context = "school=${normalizedSchoolId ?: "<any>"} forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedAcademicYearsAnalytics?.takeIf { it.key == cacheKey }?.analytics,
            fetchValue = { KtorApiClient.getAcademicYearAnalytics(normalizedSchoolId) },
            updateCache = { analytics, fetchedAt ->
                cachedAcademicYearsAnalytics = CachedAcademicYearsAnalytics(
                    key = cacheKey,
                    analytics = CachedValue(analytics, fetchedAt)
                )
            },
            successDetails = { analytics ->
                "total=${analytics.metrics.totalAcademicYears} active=${analytics.metrics.activeYears} inactive=${analytics.metrics.inactiveYears} closed=${analytics.metrics.closedYears} upcoming=${analytics.metrics.upcomingYears} current=${analytics.metrics.currentYears}"
            }
        )
    }
}
